/**
 * TemplateManagementTab
 *
 * Full template management UI with folder sidebar, template list,
 * CRUD actions, search/filter, and preview. Used inside DataDiscoveryModal.
 */

import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Loader2,
  Search,
  FolderPlus,
  FolderOpen,
  Folder,
  MoreVertical,
  Pencil,
  Copy,
  Trash2,
  FolderInput,
  ChevronDown,
  ChevronRight,
  BarChart3,
  Clock,
  ArrowUpDown,
  Check,
  X,
  AlertTriangle,
  FileText,
  Tag,
  GitBranch,
  Link2
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { useTemplateManagement, FolderTreeNode } from '@/hooks/ai/useTemplateManagement';
import { getXAxisKeyFromComposite } from '@/hooks/useQueryTemplateData';
import { useEditorStore } from '@/stores/editor/editor-store';
import { useOrganizationStore } from '@/stores/organization/organization-store';
import type { QueryTemplate } from '@/services/api/queryTemplateApi';
import type { TemplateFieldSelection } from '@/types/chartDataSource';
import type { Component } from '@/types';
import { Building2 } from 'lucide-react';
import {
  buildTemplatePreviewRows,
  formatTemplateMeasureDisplayText,
} from './TemplateManagementTab.display';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface TemplateManagementTabProps {
  onSelectTemplate: (template: QueryTemplate) => void;
  onSelectTemplateField: (selection: TemplateFieldSelection) => void;
  component: Component | null;
}

type BoundSortMode = 'bound-desc' | 'bound-asc' | null;

// ---------------------------------------------------------------------------
// Recursive folder tree item
// ---------------------------------------------------------------------------

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function addTemplateId(value: unknown, ids: Set<string>) {
  if (typeof value === 'string' && value.trim()) {
    ids.add(value);
  }
}

function collectTemplateIdsFromDataSource(value: unknown, ids: Set<string>) {
  if (!isRecord(value)) return;

  addTemplateId(value.queryTemplateId, ids);

  const queryDefinition = value.queryDefinition;
  if (isRecord(queryDefinition)) {
    addTemplateId(queryDefinition.queryTemplateId, ids);
  }
}

function collectTemplateIdsFromBindingRecord(value: Record<string, unknown>, ids: Set<string>) {
  addTemplateId(value.queryTemplateId, ids);
  collectTemplateIdsFromDataSource(value.dataSource, ids);

  const queryDefinition = value.queryDefinition;
  if (isRecord(queryDefinition)) {
    addTemplateId(queryDefinition.queryTemplateId, ids);
  }
}

function collectTemplateIdsFromBindingList(value: unknown, ids: Set<string>) {
  if (!Array.isArray(value)) return;

  for (const item of value) {
    if (isRecord(item)) {
      collectTemplateIdsFromBindingRecord(item, ids);
    }
  }
}

function getComponentTemplateIds(component: Component): Set<string> {
  const ids = new Set<string>();
  const { properties } = component;

  collectTemplateIdsFromBindingRecord(properties, ids);
  collectTemplateIdsFromBindingList(properties.dataBindings, ids);

  const pins = properties.pins;
  if (Array.isArray(pins)) {
    for (const pin of pins) {
      if (isRecord(pin)) {
        collectTemplateIdsFromBindingList(pin.dataBindings, ids);
      }
    }
  }

  return ids;
}

interface FolderTreeItemProps {
  node: FolderTreeNode;
  depth: number;
  mgmt: ReturnType<typeof useTemplateManagement>;
  renamingFolderName: string | null;
  setRenamingFolderName: (v: string | null) => void;
  folderRenameValue: string;
  setFolderRenameValue: (v: string) => void;
  folderRenameInputRef: React.RefObject<HTMLInputElement>;
  subfolderParent: string | null;
  setSubfolderParent: (v: string | null) => void;
  subfolderName: string;
  setSubfolderName: (v: string) => void;
  subfolderInputRef: React.RefObject<HTMLInputElement>;
  handleDeleteFolder: (path: string) => void;
}

function FolderTreeItem({
  node,
  depth,
  mgmt,
  renamingFolderName,
  setRenamingFolderName,
  folderRenameValue,
  setFolderRenameValue,
  folderRenameInputRef,
  subfolderParent,
  setSubfolderParent,
  subfolderName,
  setSubfolderName,
  subfolderInputRef,
  handleDeleteFolder
}: FolderTreeItemProps) {
  const hasChildren = node.children.length > 0;
  const isExpanded = mgmt.expandedPaths.has(node.path);
  const isSelected = mgmt.selectedFolder === node.path;
  const indent = depth * 12;

  return (
    <div>
      {renamingFolderName === node.path ? (
        <div className="flex items-center gap-1 px-1 py-1" style={{ paddingLeft: indent }}>
          <input
            ref={folderRenameInputRef}
            value={folderRenameValue}
            onChange={(e) => setFolderRenameValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && folderRenameValue.trim()) {
                mgmt.renameFolder(node.path, folderRenameValue.trim());
                setRenamingFolderName(null);
              }
              if (e.key === 'Escape') setRenamingFolderName(null);
            }}
            className="w-full rounded border border-emerald-300 dark:border-emerald-700 px-1.5 py-0.5 text-xs dark:bg-gray-800 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-emerald-500"
          />
        </div>
      ) : (
        <div className="group relative">
          <button
            onClick={() => mgmt.setSelectedFolder(node.path)}
            className={`flex items-center gap-1 rounded-md py-1.5 text-xs w-full text-left pr-6 ${
              isSelected
                ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 font-medium'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50'
            }`}
            style={{ paddingLeft: 8 + indent }}
          >
            {hasChildren ? (
              <span
                role="button"
                onClick={(e) => {
                  e.stopPropagation();
                  mgmt.toggleFolderExpand(node.path);
                }}
                className="p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-600 shrink-0"
              >
                {isExpanded ? (
                  <ChevronDown className="h-3 w-3 text-gray-400 dark:text-gray-500" />
                ) : (
                  <ChevronRight className="h-3 w-3 text-gray-400 dark:text-gray-500" />
                )}
              </span>
            ) : (
              <span className="w-4 shrink-0" />
            )}
            <Folder className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate flex-1">{node.name}</span>
            <span className="text-[10px] text-gray-400 dark:text-gray-500 shrink-0">{node.totalCount}</span>
          </button>

          {/* Folder context menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="absolute right-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-600">
                <MoreVertical className="h-3 w-3 text-gray-400 dark:text-gray-500" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-36">
              {/* Add Subfolder (max depth 3 = index 2) */}
              {depth < 2 && (
                <DropdownMenuItem
                  onClick={() => {
                    setSubfolderParent(node.path);
                    setSubfolderName('');
                  }}
                  className="text-xs"
                >
                  <FolderPlus className="h-3 w-3 mr-1.5" />
                  Add Subfolder
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={() => {
                  setRenamingFolderName(node.path);
                  setFolderRenameValue(node.name);
                }}
                className="text-xs"
              >
                <Pencil className="h-3 w-3 mr-1.5" />
                Rename
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleDeleteFolder(node.path)} className="text-xs text-red-600 focus:text-red-600">
                <Trash2 className="h-3 w-3 mr-1.5" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {/* Subfolder creation input */}
      {subfolderParent === node.path && (
        <div className="flex items-center gap-1 px-1 py-1" style={{ paddingLeft: indent + 12 }}>
          <input
            ref={subfolderInputRef}
            value={subfolderName}
            onChange={(e) => setSubfolderName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && subfolderName.trim()) {
                mgmt.createSubfolder(node.path, subfolderName.trim());
                setSubfolderName('');
                setSubfolderParent(null);
              }
              if (e.key === 'Escape') {
                setSubfolderName('');
                setSubfolderParent(null);
              }
            }}
            onBlur={() => {
              setSubfolderName('');
              setSubfolderParent(null);
            }}
            placeholder="Subfolder name"
            className="w-full rounded border border-emerald-300 dark:border-emerald-700 px-1.5 py-0.5 text-xs placeholder:text-gray-400 dark:placeholder:text-gray-500 dark:bg-gray-800 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-emerald-500"
          />
        </div>
      )}

      {/* Recursively render children when expanded */}
      {isExpanded &&
        node.children.map((child) => (
          <FolderTreeItem
            key={child.path}
            node={child}
            depth={depth + 1}
            mgmt={mgmt}
            renamingFolderName={renamingFolderName}
            setRenamingFolderName={setRenamingFolderName}
            folderRenameValue={folderRenameValue}
            setFolderRenameValue={setFolderRenameValue}
            folderRenameInputRef={folderRenameInputRef}
            subfolderParent={subfolderParent}
            setSubfolderParent={setSubfolderParent}
            subfolderName={subfolderName}
            setSubfolderName={setSubfolderName}
            subfolderInputRef={subfolderInputRef}
            handleDeleteFolder={handleDeleteFolder}
          />
        ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function TemplateManagementTab({ onSelectTemplate, onSelectTemplateField }: TemplateManagementTabProps) {
  // Cross-org template browsing in personal space
  const orgState = useOrganizationStore();
  const isPersonalSpace = orgState.currentContext?.type === 'user';
  const userOrgs = orgState.organizations;
  // Default to first org so templates always load (templates always belong to an org)
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(
    null // will be initialized in effect below
  );

  // Initialize selected org to first available org in personal space
  useEffect(() => {
    if (isPersonalSpace && !selectedOrgId && userOrgs.length > 0) {
      setSelectedOrgId(userOrgs[0]._id);
    }
  }, [isPersonalSpace, selectedOrgId, userOrgs]);

  // Pass selected org to hook so it fetches templates from that org
  const mgmt = useTemplateManagement(isPersonalSpace ? selectedOrgId : null);
  const pageComponents = useEditorStore((state) => state.pageComponents);
  const currentComponents = useEditorStore((state) => state.components);
  const [boundSortMode, setBoundSortMode] = useState<BoundSortMode>(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [showNewFolderInput, setShowNewFolderInput] = useState(false);
  const [renamingFolderName, setRenamingFolderName] = useState<string | null>(null);
  const [folderRenameValue, setFolderRenameValue] = useState('');
  const [inlineRenameValue, setInlineRenameValue] = useState('');
  const renameInputRef = useRef<HTMLInputElement>(null);
  const folderRenameInputRef = useRef<HTMLInputElement>(null);
  const newFolderInputRef = useRef<HTMLInputElement>(null);

  // Edit form state
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    tags: '',
    folder: ''
  });
  const [templateToDelete, setTemplateToDelete] = useState<QueryTemplate | null>(null);

  // Focus rename input when editing
  useEffect(() => {
    if (mgmt.renamingTemplateId && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [mgmt.renamingTemplateId]);

  useEffect(() => {
    if (renamingFolderName && folderRenameInputRef.current) {
      folderRenameInputRef.current.focus();
      folderRenameInputRef.current.select();
    }
  }, [renamingFolderName]);

  useEffect(() => {
    if (showNewFolderInput && newFolderInputRef.current) {
      newFolderInputRef.current.focus();
    }
  }, [showNewFolderInput]);

  // Populate edit form when entering edit mode
  useEffect(() => {
    if (mgmt.editingTemplateId) {
      const t = mgmt.templates.find((t) => t.id === mgmt.editingTemplateId);
      if (t) {
        setEditForm({
          name: t.name,
          description: t.description || '',
          tags: t.tags?.join(', ') || '',
          folder: t.folder || ''
        });
      }
    }
  }, [mgmt.editingTemplateId, mgmt.templates]);

  const activeBindingCounts = useMemo(() => {
    const counts = new Map<string, number>();
    const allComponents = pageComponents.size > 0 ? Array.from(pageComponents.values()).flat() : currentComponents;
    const countedComponentIds = new Set<string>();

    for (const editorComponent of allComponents) {
      if (editorComponent.isDeleted || countedComponentIds.has(editorComponent.id)) {
        continue;
      }

      countedComponentIds.add(editorComponent.id);
      const templateIds = getComponentTemplateIds(editorComponent);

      for (const templateId of templateIds) {
        counts.set(templateId, (counts.get(templateId) || 0) + 1);
      }
    }

    return counts;
  }, [currentComponents, pageComponents]);

  const uncategorizedCount = mgmt.templates.filter((t) => !t.folder).length;

  // Subfolder creation state
  const [subfolderParent, setSubfolderParent] = useState<string | null>(null);
  const [subfolderName, setSubfolderName] = useState('');
  const subfolderInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (subfolderParent && subfolderInputRef.current) {
      subfolderInputRef.current.focus();
    }
  }, [subfolderParent]);

  // Filter templates by selected folder (prefix match for nested) then by tags.
  // Bound-count sorting is local because bound counts come from editor state.
  const filteredTemplates = useMemo(() => {
    let filtered =
      mgmt.selectedFolder === null
        ? mgmt.templates
        : mgmt.selectedFolder === ''
          ? mgmt.templates.filter((t) => !t.folder)
          : mgmt.templates.filter((t) => t.folder === mgmt.selectedFolder || t.folder?.startsWith(mgmt.selectedFolder + '/'));
    if (mgmt.selectedTags.length > 0) {
      filtered = filtered.filter((t) => mgmt.selectedTags.every((tag) => t.tags?.includes(tag)));
    }

    if (boundSortMode) {
      const direction = boundSortMode === 'bound-desc' ? -1 : 1;
      filtered = [...filtered].sort((a, b) => {
        const boundDelta = (activeBindingCounts.get(a.id) || 0) - (activeBindingCounts.get(b.id) || 0);
        if (boundDelta !== 0) return boundDelta * direction;

        const usageDelta = (a.usageCount || 0) - (b.usageCount || 0);
        if (usageDelta !== 0) return usageDelta * direction;

        return a.name.localeCompare(b.name);
      });
    }

    return filtered;
  }, [activeBindingCounts, boundSortMode, mgmt.selectedFolder, mgmt.selectedTags, mgmt.templates]);

  /** Flatten folder tree for move-to-folder dropdown */
  const flattenTree = (nodes: FolderTreeNode[], depth = 0): Array<{ node: FolderTreeNode; depth: number }> => {
    const result: Array<{ node: FolderTreeNode; depth: number }> = [];
    for (const n of nodes) {
      result.push({ node: n, depth });
      result.push(...flattenTree(n.children, depth + 1));
    }
    return result;
  };

  /** Find a node in the tree by path */
  const findNode = (nodes: FolderTreeNode[], path: string): FolderTreeNode | undefined => {
    for (const n of nodes) {
      if (n.path === path) return n;
      const found = findNode(n.children, path);
      if (found) return found;
    }
    return undefined;
  };

  /** Count all descendant folders */
  const countDescendantFolders = (node: FolderTreeNode): number =>
    node.children.length + node.children.reduce((sum, c) => sum + countDescendantFolders(c), 0);

  // Start inline rename
  const startRename = (template: QueryTemplate) => {
    setInlineRenameValue(template.name);
    mgmt.setRenamingTemplateId(template.id);
  };

  const confirmRename = () => {
    if (mgmt.renamingTemplateId && inlineRenameValue.trim()) {
      mgmt.renameTemplate(mgmt.renamingTemplateId, inlineRenameValue.trim());
    }
  };

  const cancelRename = () => {
    mgmt.setRenamingTemplateId(null);
    setInlineRenameValue('');
  };

  // Start edit
  const startEdit = (template: QueryTemplate) => {
    mgmt.setEditingTemplateId(template.id);
    mgmt.setRenamingTemplateId(null);
  };

  const handleSaveEdit = () => {
    if (!mgmt.editingTemplateId) return;
    mgmt.updateTemplate(mgmt.editingTemplateId, {
      name: editForm.name.trim(),
      description: editForm.description.trim() || undefined,
      tags: editForm.tags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean),
      folder: editForm.folder.trim() || undefined
    });
  };

  // Handle delete with confirmation
  const handleDelete = (template: QueryTemplate) => {
    setTemplateToDelete(template);
  };

  const confirmDeleteTemplate = async () => {
    if (!templateToDelete) return;
    const templateId = templateToDelete.id;
    setTemplateToDelete(null);
    await mgmt.deleteTemplate(templateId);
  };

  // Handle folder delete with confirmation (tree-aware)
  const handleDeleteFolder = (folderPath: string) => {
    const node = findNode(mgmt.folderTree, folderPath);
    const totalTemplates = node?.totalCount || 0;
    const childFolderCount = node ? countDescendantFolders(node) : 0;
    const leafName = folderPath.split('/').pop() || folderPath;

    let msg = `Delete folder "${leafName}"?`;
    if (childFolderCount > 0) {
      msg += ` This will also delete ${childFolderCount} subfolder(s).`;
    }
    if (totalTemplates > 0) {
      msg += ` ${totalTemplates} template(s) will be moved to Uncategorized.`;
    }
    if (window.confirm(msg)) {
      mgmt.deleteFolder(folderPath);
    }
  };

  // Format relative time
  const formatRelativeTime = (dateStr?: string) => {
    if (!dateStr) return 'Never';
    const d = new Date(dateStr);
    const diff = Date.now() - d.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  return (
    <div className="flex flex-col h-full">
      {/* Org selector — only in personal space */}
      {isPersonalSpace && userOrgs.length > 0 && (
        <div className="flex items-center gap-2 px-4 pt-3 pb-2 border-b border-gray-100 dark:border-gray-700">
          <Building2 className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500 shrink-0" />
          <select
            value={selectedOrgId ?? ''}
            onChange={(e) => setSelectedOrgId(e.target.value || null)}
            className="flex-1 rounded-md border border-gray-200 dark:border-gray-700 py-1.5 px-2 text-sm text-gray-700 dark:text-gray-300 dark:bg-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          >
            {userOrgs.map((org) => (
              <option key={org._id} value={org._id}>
                {org.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Top bar: search + sort */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 dark:border-gray-700">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
          <input
            type="text"
            value={mgmt.searchQuery}
            onChange={(e) => mgmt.setSearchQuery(e.target.value)}
            placeholder="Search templates..."
            className="w-full rounded-md border border-gray-200 dark:border-gray-700 py-1.5 pl-8 pr-3 text-sm placeholder:text-gray-400 dark:placeholder:text-gray-500 dark:bg-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          />
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 dark:border-gray-700 px-2.5 py-1.5 text-xs text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50">
              <ArrowUpDown className="h-3.5 w-3.5" />
              Sort
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-40">
            <DropdownMenuItem
              onClick={() => {
                setBoundSortMode('bound-desc');
              }}
              className="text-xs"
            >
              Most Bound
              {boundSortMode === 'bound-desc' && <Check className="ml-auto h-3 w-3" />}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => {
                setBoundSortMode('bound-asc');
              }}
              className="text-xs"
            >
              Least Bound
              {boundSortMode === 'bound-asc' && <Check className="ml-auto h-3 w-3" />}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => {
                setBoundSortMode(null);
                mgmt.setSortBy('usageCount');
                mgmt.setSortOrder('desc');
              }}
              className="text-xs"
            >
              Most Used
              {!boundSortMode && mgmt.sortBy === 'usageCount' && mgmt.sortOrder === 'desc' && <Check className="ml-auto h-3 w-3" />}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => {
                setBoundSortMode(null);
                mgmt.setSortBy('createdAt');
                mgmt.setSortOrder('desc');
              }}
              className="text-xs"
            >
              Newest
              {!boundSortMode && mgmt.sortBy === 'createdAt' && mgmt.sortOrder === 'desc' && <Check className="ml-auto h-3 w-3" />}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => {
                setBoundSortMode(null);
                mgmt.setSortBy('name');
                mgmt.setSortOrder('asc');
              }}
              className="text-xs"
            >
              Name A-Z
              {!boundSortMode && mgmt.sortBy === 'name' && mgmt.sortOrder === 'asc' && <Check className="ml-auto h-3 w-3" />}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => {
                setBoundSortMode(null);
                mgmt.setSortBy('name');
                mgmt.setSortOrder('desc');
              }}
              className="text-xs"
            >
              Name Z-A
              {!boundSortMode && mgmt.sortBy === 'name' && mgmt.sortOrder === 'desc' && <Check className="ml-auto h-3 w-3" />}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Tag filter bar */}
      {mgmt.allTags.length > 0 && (
        <div className="flex items-center gap-1.5 px-4 py-2 border-b border-gray-100 dark:border-gray-700 flex-wrap">
          <Tag className="h-3 w-3 text-gray-400 dark:text-gray-500 shrink-0" />
          {mgmt.allTags.map((tag) => {
            const isActive = mgmt.selectedTags.includes(tag);
            return (
              <button
                key={tag}
                onClick={() => mgmt.toggleTag(tag)}
                className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] border transition-colors ${
                  isActive
                    ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border-emerald-300 dark:border-emerald-700'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border-transparent hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                {tag}
              </button>
            );
          })}
          {mgmt.selectedTags.length > 0 && (
            <button
              onClick={() => mgmt.setSelectedTags([])}
              className="inline-flex items-center gap-0.5 text-[10px] text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 ml-1"
            >
              <X className="h-3 w-3" />
              Clear
            </button>
          )}
        </div>
      )}

      {/* Main content: sidebar + list */}
      <div className="flex flex-1 min-h-0">
        {/* Folder sidebar */}
        <div className="w-40 border-r border-gray-100 dark:border-gray-700 flex flex-col py-2 px-2 shrink-0 overflow-y-auto">
          {/* All */}
          <button
            onClick={() => mgmt.setSelectedFolder(null)}
            className={`flex items-center gap-1.5 rounded-md px-2 py-1.5 text-xs w-full text-left ${
              mgmt.selectedFolder === null
                ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 font-medium'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50'
            }`}
          >
            <FolderOpen className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate flex-1">All</span>
            <span className="text-[10px] text-gray-400 dark:text-gray-500">{mgmt.total}</span>
          </button>

          {/* Folder tree (recursive) */}
          {mgmt.folderTree.map((node) => (
            <FolderTreeItem
              key={node.path}
              node={node}
              depth={0}
              mgmt={mgmt}
              renamingFolderName={renamingFolderName}
              setRenamingFolderName={setRenamingFolderName}
              folderRenameValue={folderRenameValue}
              setFolderRenameValue={setFolderRenameValue}
              folderRenameInputRef={folderRenameInputRef}
              subfolderParent={subfolderParent}
              setSubfolderParent={setSubfolderParent}
              subfolderName={subfolderName}
              setSubfolderName={setSubfolderName}
              subfolderInputRef={subfolderInputRef}
              handleDeleteFolder={handleDeleteFolder}
            />
          ))}

          {/* Uncategorized */}
          <button
            onClick={() => mgmt.setSelectedFolder('')}
            className={`flex items-center gap-1.5 rounded-md px-2 py-1.5 text-xs w-full text-left ${
              mgmt.selectedFolder === ''
                ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 font-medium'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50'
            }`}
          >
            <FolderOpen className="h-3.5 w-3.5 shrink-0 text-gray-400 dark:text-gray-500" />
            <span className="truncate flex-1 italic">Uncategorized</span>
            <span className="text-[10px] text-gray-400 dark:text-gray-500">{uncategorizedCount}</span>
          </button>

          {/* New folder */}
          {showNewFolderInput ? (
            <div className="flex items-center gap-1 px-1 py-1 mt-1">
              <input
                ref={newFolderInputRef}
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newFolderName.trim()) {
                    mgmt.createFolder(newFolderName.trim());
                    setNewFolderName('');
                    setShowNewFolderInput(false);
                  }
                  if (e.key === 'Escape') {
                    setNewFolderName('');
                    setShowNewFolderInput(false);
                  }
                }}
                placeholder="Folder name"
                className="w-full rounded border border-emerald-300 dark:border-emerald-700 px-1.5 py-0.5 text-xs placeholder:text-gray-400 dark:placeholder:text-gray-500 dark:bg-gray-800 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-emerald-500"
              />
            </div>
          ) : (
            <button
              onClick={() => setShowNewFolderInput(true)}
              className="flex items-center gap-1.5 rounded-md px-2 py-1.5 text-xs text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 mt-1"
            >
              <FolderPlus className="h-3.5 w-3.5" />
              New Folder
            </button>
          )}
        </div>

        {/* Template list */}
        <div className="flex-1 overflow-y-auto">
          {mgmt.isLoading && filteredTemplates.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-2">
              <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
              <p className="text-xs text-gray-500 dark:text-gray-400">Loading templates...</p>
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <FileText className="h-8 w-8 text-gray-300 dark:text-gray-600" />
              <p className="text-sm text-gray-400 dark:text-gray-500">No templates found</p>
              <p className="text-xs text-gray-400 dark:text-gray-500">Use the Create tab to build templates with AI</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {filteredTemplates.map((template) => (
                <div key={template.id}>
                  {/* Template row */}
                  {(() => {
                    const activeBindingCount = activeBindingCounts.get(template.id) || 0;
                    const boundLabel = `${activeBindingCount} bound`;

                    return (
                      <div
                        className={`flex items-start gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer ${
                          mgmt.expandedTemplateId === template.id ? 'bg-gray-50 dark:bg-gray-800/50' : ''
                        }`}
                        onClick={() => mgmt.togglePreview(template.id)}
                      >
                        <div className="flex-1 min-w-0">
                          {/* Name (inline rename or display) */}
                          {mgmt.renamingTemplateId === template.id ? (
                            <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                              <input
                                ref={renameInputRef}
                                value={inlineRenameValue}
                                onChange={(e) => setInlineRenameValue(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') confirmRename();
                                  if (e.key === 'Escape') cancelRename();
                                }}
                                className="rounded border border-emerald-300 dark:border-emerald-700 px-1.5 py-0.5 text-sm font-medium dark:bg-gray-800 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-emerald-500 w-full max-w-xs"
                              />
                              <button onClick={confirmRename} className="p-0.5 rounded hover:bg-emerald-100 dark:hover:bg-emerald-900/30">
                                <Check className="h-3.5 w-3.5 text-emerald-600" />
                              </button>
                              <button onClick={cancelRename} className="p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-600">
                                <X className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              {mgmt.expandedTemplateId === template.id ? (
                                <ChevronDown className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500 shrink-0" />
                              ) : (
                                <ChevronRight className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500 shrink-0" />
                              )}
                              <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{template.name}</span>
                            </div>
                          )}

                          {/* Description */}
                          {template.description && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate pl-5">{template.description}</p>
                          )}

                          {template.templateType === 'composite-query' && (
                            <div className="pl-5 mt-1">
                              <span className="inline-flex items-center gap-1 rounded-full bg-sky-100 px-1.5 py-0.5 text-[10px] text-sky-700">
                                <GitBranch className="h-2.5 w-2.5" />
                                Composite Query
                              </span>
                            </div>
                          )}

                          {/* Tags + folder */}
                          <div className="flex items-center gap-1.5 mt-1 pl-5 flex-wrap">
                            {template.tags?.map((tag) => (
                              <button
                                key={tag}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  mgmt.toggleTag(tag);
                                }}
                                className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] border transition-colors ${
                                  mgmt.selectedTags.includes(tag)
                                    ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border-emerald-300 dark:border-emerald-700'
                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border-transparent hover:border-gray-300 dark:hover:border-gray-600'
                                }`}
                              >
                                {tag}
                              </button>
                            ))}
                            {template.folder && (
                              <span
                                className="inline-flex items-center gap-0.5 text-[10px] text-gray-400 dark:text-gray-500"
                                title={template.folder}
                              >
                                <Folder className="h-2.5 w-2.5" />
                                {template.folder.includes('/')
                                  ? template.folder.split('/').map((seg, i) => (
                                      <React.Fragment key={i}>
                                        {i > 0 && <ChevronRight className="h-2 w-2" />}
                                        <span>{seg}</span>
                                      </React.Fragment>
                                    ))
                                  : template.folder}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Right side: usage + actions */}
                        <div className="flex items-center gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
                          <div className="text-right">
                            <div
                              className="flex items-center justify-end gap-1 text-[10px] font-medium text-emerald-600 dark:text-emerald-400"
                              title="Components currently bound to this template"
                            >
                              <Link2 className="h-3 w-3" />
                              {boundLabel}
                            </div>
                            <div
                              className="flex items-center justify-end gap-1 text-[10px] text-gray-400 dark:text-gray-500 mt-0.5"
                              title="Historical template executions"
                            >
                              <BarChart3 className="h-3 w-3" />
                              {template.usageCount} runs
                            </div>
                            <div className="flex items-center justify-end gap-1 text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">
                              <Clock className="h-3 w-3" />
                              {formatRelativeTime(template.lastUsedAt)}
                            </div>
                          </div>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600">
                                <MoreVertical className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-40">
                              <DropdownMenuItem onClick={() => startRename(template)} className="text-xs">
                                <Pencil className="h-3 w-3 mr-1.5" />
                                Rename
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => startEdit(template)} className="text-xs">
                                <Pencil className="h-3 w-3 mr-1.5" />
                                Edit Details
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => mgmt.duplicateTemplate(template)} className="text-xs">
                                <Copy className="h-3 w-3 mr-1.5" />
                                Duplicate
                              </DropdownMenuItem>
                              <DropdownMenuSub>
                                <DropdownMenuSubTrigger className="text-xs">
                                  <FolderInput className="h-3 w-3 mr-1.5" />
                                  Move to Folder
                                </DropdownMenuSubTrigger>
                                <DropdownMenuSubContent className="w-44">
                                  <DropdownMenuItem onClick={() => mgmt.moveToFolder(template.id, undefined)} className="text-xs italic">
                                    Uncategorized
                                  </DropdownMenuItem>
                                  {flattenTree(mgmt.folderTree).map(({ node, depth }) => (
                                    <DropdownMenuItem
                                      key={node.path}
                                      onClick={() => mgmt.moveToFolder(template.id, node.path)}
                                      className="text-xs"
                                      style={{ paddingLeft: 8 + depth * 12 }}
                                    >
                                      {node.name}
                                      {template.folder === node.path && <Check className="ml-auto h-3 w-3" />}
                                    </DropdownMenuItem>
                                  ))}
                                </DropdownMenuSubContent>
                              </DropdownMenuSub>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => handleDelete(template)} className="text-xs text-red-600 focus:text-red-600">
                                <Trash2 className="h-3 w-3 mr-1.5" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Expanded preview */}
                  {mgmt.expandedTemplateId === template.id && (
                    <div className="px-4 pb-3 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-100 dark:border-gray-700">
                      {/* Measures */}
                      {template.query?.measures && template.query.measures.length > 0 && (
                        <div className="mb-2">
                          <p className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Measures</p>
                          <div className="flex flex-wrap gap-1.5">
                            {template.query.measures.map((m, i) => (
                              <button
                                key={i}
                                onClick={() =>
                                  onSelectTemplateField({
                                    fieldName: m.field,
                                    aggregation: m.aggregation,
                                    alias: m.alias,
                                    schemaId: template.query?.schemaId ?? '',
                                    groupId: template.query?.groupId,
                                    templateId: template.id,
                                    templateName: template.name
                                  })
                                }
                                className="inline-flex items-center gap-1 rounded-full bg-amber-100 dark:bg-amber-900/30 px-2 py-0.5 text-xs text-amber-700 dark:text-amber-400 hover:bg-amber-200 dark:hover:bg-amber-800/40 transition-colors"
                              >
                                {formatTemplateMeasureDisplayText(m)}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {(() => {
                        const compositeDef = template.templateType === 'composite-query' ? template.compositeQueryDefinition : undefined;

                        if (!compositeDef) {
                          return null;
                        }

                        return (
                          <div className="mb-2">
                            <p className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                              Composite
                            </p>
                            <div className="text-xs text-gray-600 dark:text-gray-400">
                              {compositeDef.sources?.length || 0} sources
                              {' • '}
                              {compositeDef.fields?.map((field) => field.alias).join(', ') || 'No projected fields'}
                            </div>
                            {(() => {
                              const xAxisKey = getXAxisKeyFromComposite(compositeDef);
                              const selectableFields = Array.from(
                                new Set(
                                  [
                                    ...(compositeDef.fields || []).map((field) => field.alias),
                                    ...(compositeDef.computedFields || []).map((field) => field.name)
                                  ].filter((field) => field && field !== xAxisKey)
                                )
                              );
                              const firstSource = compositeDef.sources?.[0];

                              if (!selectableFields.length || !firstSource?.query?.schemaId) {
                                return null;
                              }

                              return (
                                <div className="mt-2">
                                  <p className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                                    Fields
                                  </p>
                                  <div className="flex flex-wrap gap-1.5">
                                    {selectableFields.map((fieldName) => (
                                      <button
                                        key={fieldName}
                                        onClick={() =>
                                          onSelectTemplateField({
                                            fieldName,
                                            alias: fieldName,
                                            schemaId: firstSource.query.schemaId,
                                            groupId: firstSource.query.groupId,
                                            templateId: template.id,
                                            templateName: template.name,
                                            organizationId: template.organizationId,
                                            compositeQueryDefinition: compositeDef
                                          })
                                        }
                                        className="inline-flex items-center gap-1 rounded-full bg-emerald-100 dark:bg-emerald-900/30 px-2 py-0.5 text-xs text-emerald-700 dark:text-emerald-400 hover:bg-emerald-200 dark:hover:bg-emerald-800/40 transition-colors"
                                      >
                                        {fieldName}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        );
                      })()}

                      {/* Data preview */}
                      {mgmt.isPreviewLoading && (
                        <div className="flex items-center gap-2 py-2">
                          <Loader2 className="h-3.5 w-3.5 animate-spin text-emerald-600" />
                          <span className="text-xs text-gray-500 dark:text-gray-400">Loading preview...</span>
                        </div>
                      )}
                      {mgmt.previewData && mgmt.previewData.length > 0 && (() => {
                        const previewRows = buildTemplatePreviewRows(mgmt.previewData);
                        const previewColumns = previewRows[0] ?? [];

                        if (previewColumns.length === 0) {
                          return null;
                        }

                        return (
                          <div className="mt-2">
                            <p className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                              Data Preview
                            </p>
                            <div className="overflow-x-auto rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="bg-gray-50 dark:bg-gray-800/50">
                                    {previewColumns.map((cell) => (
                                      <th
                                        key={cell.key}
                                        className="text-left py-1 px-2 font-medium text-gray-600 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700"
                                      >
                                        {cell.label}
                                      </th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {previewRows.map((row) => {
                                    const rowKey = row.map((cell) => `${cell.key}:${cell.value}`).join('|');
                                    return (
                                      <tr key={rowKey} className="border-b border-gray-100 dark:border-gray-700 last:border-0">
                                        {row.map((cell) => (
                                          <td key={cell.key} className="py-1 px-2 text-gray-500 dark:text-gray-400">
                                            {cell.value}
                                          </td>
                                        ))}
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        );
                      })()}

                      {/* Use template button */}
                      <div className="mt-2 flex gap-2">
                        <button
                          onClick={() => onSelectTemplate(template)}
                          className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700"
                        >
                          Use Template
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Inline edit form */}
                  {mgmt.editingTemplateId === template.id && (
                    <div className="px-4 pb-3 bg-blue-50/50 border-b border-blue-100" onClick={(e) => e.stopPropagation()}>
                      <div className="grid grid-cols-2 gap-2 pt-2">
                        <div>
                          <label className="block text-[10px] font-medium text-gray-600 dark:text-gray-400 mb-0.5">Name</label>
                          <input
                            type="text"
                            value={editForm.name}
                            onChange={(e) =>
                              setEditForm((p) => ({
                                ...p,
                                name: e.target.value
                              }))
                            }
                            className="w-full rounded border border-gray-300 dark:border-gray-600 px-2 py-1 text-xs dark:bg-gray-800 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] font-medium text-gray-600 dark:text-gray-400 mb-0.5">Description</label>
                          <input
                            type="text"
                            value={editForm.description}
                            onChange={(e) =>
                              setEditForm((p) => ({
                                ...p,
                                description: e.target.value
                              }))
                            }
                            className="w-full rounded border border-gray-300 dark:border-gray-600 px-2 py-1 text-xs dark:bg-gray-800 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] font-medium text-gray-600 dark:text-gray-400 mb-0.5">
                            Tags (comma-separated)
                          </label>
                          <input
                            type="text"
                            value={editForm.tags}
                            onChange={(e) =>
                              setEditForm((p) => ({
                                ...p,
                                tags: e.target.value
                              }))
                            }
                            className="w-full rounded border border-gray-300 dark:border-gray-600 px-2 py-1 text-xs dark:bg-gray-800 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                          />
                          {/* Tag suggestions */}
                          {(() => {
                            const currentTags = editForm.tags
                              .split(',')
                              .map((t) => t.trim())
                              .filter(Boolean);
                            const suggestions = mgmt.allTags.filter((t) => !currentTags.includes(t));
                            if (suggestions.length === 0) return null;
                            return (
                              <div className="flex items-center gap-1 mt-1 flex-wrap">
                                <span className="text-[10px] text-gray-400 dark:text-gray-500">Add:</span>
                                {suggestions.map((tag) => (
                                  <button
                                    key={tag}
                                    type="button"
                                    onClick={() => {
                                      const updated = currentTags.length > 0 ? `${editForm.tags.trimEnd()}, ${tag}` : tag;
                                      setEditForm((p) => ({
                                        ...p,
                                        tags: updated
                                      }));
                                    }}
                                    className="inline-flex items-center rounded-full bg-gray-50 dark:bg-gray-800/50 border border-dashed border-gray-300 dark:border-gray-600 px-1.5 py-0.5 text-[10px] text-gray-500 dark:text-gray-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 hover:border-emerald-300 dark:hover:border-emerald-700 hover:text-emerald-600 transition-colors"
                                  >
                                    + {tag}
                                  </button>
                                ))}
                              </div>
                            );
                          })()}
                        </div>
                        <div>
                          <label className="block text-[10px] font-medium text-gray-600 dark:text-gray-400 mb-0.5">Folder</label>
                          <select
                            value={editForm.folder}
                            onChange={(e) =>
                              setEditForm((p) => ({
                                ...p,
                                folder: e.target.value
                              }))
                            }
                            className="w-full rounded border border-gray-300 dark:border-gray-600 px-2 py-1 text-xs dark:bg-gray-800 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                          >
                            <option value="">Uncategorized</option>
                            {flattenTree(mgmt.folderTree).map(({ node, depth }) => (
                              <option key={node.path} value={node.path}>
                                {'\u00A0\u00A0'.repeat(depth)}
                                {node.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <button
                          onClick={handleSaveEdit}
                          disabled={!editForm.name.trim() || mgmt.isOperating}
                          className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                        >
                          {mgmt.isOperating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                          Save
                        </button>
                        <button
                          onClick={() => mgmt.setEditingTemplateId(null)}
                          className="inline-flex items-center gap-1 rounded-md border border-gray-300 dark:border-gray-600 px-2.5 py-1 text-xs text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                        >
                          <X className="h-3 w-3" />
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Operating overlay */}
      {mgmt.isOperating && (
        <div className="absolute inset-0 bg-white/50 dark:bg-gray-900/50 flex items-center justify-center z-10 pointer-events-none">
          <Loader2 className="h-5 w-5 animate-spin text-emerald-600" />
        </div>
      )}

      <AlertDialog open={templateToDelete !== null} onOpenChange={(open) => !open && setTemplateToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              <span>Delete Template</span>
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{templateToDelete?.name}&quot;?
              <span className="block mt-2">
                This action cannot be undone.
                {templateToDelete && (activeBindingCounts.get(templateToDelete.id) || 0) > 0
                  ? ` This template is currently bound to ${activeBindingCounts.get(templateToDelete.id)} component${
                      (activeBindingCounts.get(templateToDelete.id) || 0) === 1 ? '' : 's'
                    }.`
                  : ''}
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={mgmt.isOperating}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteTemplate}
              disabled={mgmt.isOperating}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {mgmt.isOperating ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Delete Template
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
